import React, { memo, useMemo } from "react";

import { MessageThreadFragment, MessageThreadType } from "gql/graphql";
import { Circle, HStack, Spacer, Text, VStack } from "@chakra-ui/react";
import { ChatsIcon, CircleCheckIcon, ThreadsIcon } from "modules/icons";
import colors from "app/colors";
import moment from "moment";
import { INBOX_MESSAGE_THREAD_CALENDAR_FORMATS } from "modules/utils/Constants";
import UserName from "modules/_shared/user-name";
import useMessageText from "modules/hooks/use-message-text";
import MessageThreadPreviewSubject from "modules/messaging/message-thread/message-thread-preview-subject";
import MessagePlainText from "modules/messaging/messages/message-plain-text";

interface IProps {
  messageThread: MessageThreadFragment;
}

const ForwardToMessageThread = ({ messageThread }: IProps) => {
  const lastMessage = messageThread.lastMessage;

  const { renderedText } = useMessageText(lastMessage, messageThread);

  const headerIcon = useMemo(() => {
    switch (messageThread.type) {
      case MessageThreadType.Thread:
        return (
          <Circle size={8} backgroundColor={colors.orange10}>
            <ThreadsIcon boxSize={5} fill={colors.yellow50} />
          </Circle>
        );
      case MessageThreadType.Chat:
        return (
          <Circle size={8} backgroundColor={colors.blueLight}>
            <ChatsIcon boxSize={5} fill={colors.blue10} />
          </Circle>
        );
      case MessageThreadType.Task:
        return (
          <Circle size={8} backgroundColor={colors.green20}>
            <CircleCheckIcon boxSize={5} fill={colors.green40} />
          </Circle>
        );
      default:
        return null;
    }
  }, [messageThread.type]);

  return (
    <HStack h={12} borderBottomWidth={1} borderBottomColor={colors.grayPrimary}>
      {headerIcon}
      <VStack w="full" spacing={0} alignItems="flex-start">
        <MessageThreadPreviewSubject messageThread={messageThread} />
        {messageThread.type !== MessageThreadType.Chat && (
          <HStack w="full">
            <Text
              fontSize={12}
              fontWeight={400}
              color={colors.gray50}
              noOfLines={1}
            >
              <UserName
                userId={lastMessage?.createdUserId}
                displayOnlyFirstName
              />
              : <MessagePlainText text={renderedText} linkify={false} />
            </Text>
            <Spacer />
            <Text
              fontSize="12px"
              pr={1}
              noOfLines={1}
              minW="fit-content"
              color={colors.gray50}
            >
              {moment(messageThread.sortTime).calendar(
                INBOX_MESSAGE_THREAD_CALENDAR_FORMATS,
              )}
            </Text>
          </HStack>
        )}
      </VStack>
    </HStack>
  );
};

export default memo(ForwardToMessageThread);
